import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Spin } from "antd";
import PurchaseOrderService from "../../services/purchaseOrder.service";
import StoneSummary from "./StoneSummary";

// Supplier -- matches Diamond-PO-Prototype-v3.html's sHolds() exactly (lines 889-895): who is
// holding this supplier's stones right now, whether tied to a sent request (doc number, opens
// the document) or still sitting in a retailer's basket ("basket · not sent").

const fmtMoney = (v) => `$${Number(v || 0).toLocaleString()}`;

const cdText = (expiresUtc) => {
  const ms = new Date(expiresUtc).getTime() - Date.now();
  if (ms <= 0) return { text: "expired", danger: true };
  const mins = Math.floor(ms / 60000);
  const text = mins >= 60 ? `${Math.floor(mins / 60)}h ${mins % 60}m` : `${mins}m`;
  return { text, danger: mins < 30 };
};

export const ActiveHolds = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const loginDetials = useSelector((state) => state.loginReducer);
  const dealerId = loginDetials?.loginDetials?.responseData?.dealerId;

  const [holds, setHolds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [, setTick] = useState(0);

  useEffect(() => {
    if (!dealerId) return;
    setLoading(true);
    PurchaseOrderService.GetDiamondActiveHolds({ DealerID: String(dealerId) })
      .then((response) => {
        setLoading(false);
        setHolds(response.data.status ? response.data.responseData || [] : []);
      })
      .catch(() => setLoading(false));
  }, [dealerId]);

  // Prototype refreshes the live holds screen every 25s (its own setInterval) so expiry
  // countdowns stay accurate without a manual refresh -- just re-ticking the render here is
  // enough since the data itself (ExpiresUtc) doesn't change on its own.
  useEffect(() => {
    const id = setInterval(() => setTick((v) => v + 1), 30000);
    return () => clearInterval(id);
  }, []);

  return (
    <div style={{ padding: 20 }}>
      <Spin spinning={loading}>
        <h2 style={{ margin: 0 }}>{t("Active Holds")}</h2>
        <p style={{ color: "#888", margin: 0, marginBottom: 16 }}>{t("Who is holding your stones right now")}</p>

        {holds.length === 0 ? (
          <div style={{ background: "#fff", border: "1px solid #eee", borderRadius: 8, padding: 40, textAlign: "center" }}>
            <div style={{ fontSize: 28, marginBottom: 10 }}>◇</div>
            {t("No live holds.")}
          </div>
        ) : (
          <div style={{ background: "#fff", border: "1px solid #eee", borderRadius: 8, overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ textAlign: "left", borderBottom: "1px solid #eee", color: "#6b7689", fontSize: 11, textTransform: "uppercase" }}>
                  <th style={{ padding: "10px 12px" }}>{t("Stone")}</th>
                  <th style={{ padding: "10px 12px" }}>{t("Held by")}</th>
                  <th style={{ padding: "10px 12px" }}>{t("For")}</th>
                  <th style={{ padding: "10px 12px" }}>{t("Expires")}</th>
                  <th style={{ padding: "10px 12px", textAlign: "right" }}>{t("Value")}</th>
                </tr>
              </thead>
              <tbody>
                {holds.map((h) => {
                  const cd = cdText(h.ExpiresUtc);
                  const snap = h.StoneSnapshotJson ? JSON.parse(h.StoneSnapshotJson) : null;
                  return (
                    <tr key={h.HoldID} style={{ borderBottom: "1px solid #f3f3f3" }}>
                      <td style={{ padding: "10px 12px" }}>
                        <StoneSummary snap={snap} />
                      </td>
                      <td style={{ padding: "10px 12px" }}>{h.RetailerName}</td>
                      <td style={{ padding: "10px 12px" }}>
                        {h.DocNumber ? (
                          <span style={{ color: "#2a52b8", cursor: "pointer" }} onClick={() => navigate(`/document/${h.DiamondDocID}`)}>
                            {h.DocNumber}
                          </span>
                        ) : (
                          <span style={{ color: "#888" }}>{t("basket · not sent")}</span>
                        )}
                      </td>
                      <td style={{ padding: "10px 12px", color: cd.danger ? "#c0392b" : "inherit", fontWeight: cd.danger ? 600 : 400 }}>{cd.text}</td>
                      <td style={{ padding: "10px 12px", textAlign: "right", fontWeight: 700 }}>{fmtMoney(h.Value)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Spin>
    </div>
  );
};

export default ActiveHolds;
